import { useState } from 'react'
import {
  appThemeApiRef,
  configApiRef,
  useApi,
} from '@backstage/core-plugin-api'
import { Entity } from '@backstage/catalog-model'
import {
  DG_ANNOTATIONS,
  parseEnvironmentsAnnotation,
} from '@internal/backstage-plugin-dg-catalog-common'
import useObservable from 'react-use/esm/useObservable'
import type { Tone } from '../../components'

export const DEFAULT_GRAFANA_BASE_URL = 'https://grafana.deepgaming.eu'
export const DEFAULT_SLACK_CHANNEL_BASE_URL =
  'https://deepgaming.slack.com/channels/'

export function useGrafanaBaseUrl(): string {
  return (
    useApi(configApiRef).getOptionalString('grafana.baseUrl') ??
    DEFAULT_GRAFANA_BASE_URL
  )
}

export function useSlackChannelUrl(channel: string): string {
  const base =
    useApi(configApiRef).getOptionalString('dgCatalog.slackChannelBaseUrl') ??
    DEFAULT_SLACK_CHANNEL_BASE_URL
  return `${base.replace(/\/?$/, '/')}${encodeURIComponent(
    channel.replace(/^#/, ''),
  )}`
}

export function useGrafanaTheme(): 'light' | 'dark' {
  const themeApi = useApi(appThemeApiRef)
  const activeId = useObservable(
    themeApi.activeThemeId$(),
    themeApi.getActiveThemeId(),
  )
  const themes = themeApi.getInstalledThemes()
  const active = themes.find(t => t.id === activeId)
  if (active) {
    return active.variant
  }
  // No explicit choice means the app follows the OS preference.
  const prefersDark =
    typeof window !== 'undefined' &&
    window.matchMedia?.('(prefers-color-scheme: dark)').matches
  return prefersDark && themes.some(t => t.variant === 'dark')
    ? 'dark'
    : 'light'
}

export function entityEnvironments(entity: Entity): string[] {
  return parseEnvironmentsAnnotation(
    entity.metadata.annotations?.[DG_ANNOTATIONS.environments],
  )
}

export function hasEnvironments(entity: Entity): boolean {
  return entityEnvironments(entity).length > 0
}

export function useEnvironmentSelection(entity: Entity) {
  const environments = entityEnvironments(entity)
  const [env, setEnv] = useState(
    environments.includes('prod') ? 'prod' : environments[0] ?? 'prod',
  )
  return { env, setEnv, environments }
}

export function scoreTone(score: number): Tone {
  if (score === 100) {
    return 'success'
  }
  return score >= 50 ? 'warning' : 'danger'
}
